import axios from 'axios';
import { API } from '@/config/constants';

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();

  const mainApiInstance = axios.create({
    baseURL: config.public.apiBaseUrl,
    withCredentials: false,
    timeout: API.TIMEOUT_MS,
  });

  // Track if we're currently refreshing to prevent multiple refresh attempts
  let isRefreshing = false;
  let refreshSubscribers = [];

  // Add subscribers to wait for refresh to complete
  const subscribeTokenRefresh = (callback) => {
    refreshSubscribers.push(callback);
  };

  // Notify all subscribers that refresh is complete
  const onRefreshed = (newToken) => {
    refreshSubscribers.forEach(callback => callback(newToken));
    refreshSubscribers = [];
  };

  // Attempt to refresh the token
  const refreshToken = async () => {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue) {
      throw new Error('No refresh token');
    }

    const response = await axios.post(
      `${config.public.apiBaseUrl}/auth/refresh`,
      null,
      {
        headers: {
          Authorization: `Bearer ${refreshTokenValue}`,
        },
      }
    );

    const { access_token, refresh_token } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    }
    return access_token;
  };

  const responseInterceptor = {
    success: response => response,
    error: async (error) => {
      // Handle timeout errors with a user-friendly message
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        error.isTimeout = true;
        error.userMessage = 'The request timed out. The operation may still be processing in the background. Please check the onboarding logs.';
        return Promise.reject(error);
      }

      // Handle network errors (no response from server)
      if (!error.response && error.message === 'Network Error') {
        error.isNetworkError = true;
        error.userMessage = 'Unable to connect to the server. Please check your connection and try again.';
        return Promise.reject(error);
      }

      // Handle 401 Unauthorized errors - try to refresh token
      if (error.response?.status === 401) {
        const originalRequest = error.config;
        const isAuthEndpoint = originalRequest?.url?.includes('/auth/');

        // Don't try to refresh for auth endpoints or if we already retried
        if (isAuthEndpoint || originalRequest._retry) {
          if (!isAuthEndpoint && typeof window !== 'undefined') {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            sessionStorage.setItem('sessionExpired', 'true');
          }
          return Promise.reject(error);
        }

        // If we're already refreshing, wait for it to complete
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((newToken) => {
              if (newToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(mainApiInstance(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        // Mark that we're refreshing
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const newToken = await refreshToken();
          isRefreshing = false;
          onRefreshed(newToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return mainApiInstance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshed(null);

          // Refresh failed - clear tokens and redirect
          if (typeof window !== 'undefined') {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            sessionStorage.setItem('sessionExpired', 'true');
          }
          return Promise.reject(error);
        }
      }

      // Handle 403 Forbidden errors - permission denied
      if (error.response?.status === 403) {
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('rbac:forbidden', {
            detail: {
              message: error.response?.data?.detail || 'You do not have permission to perform this action',
              url: error.config?.url,
              method: error.config?.method
            }
          });
          window.dispatchEvent(event);
        }
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  };

  mainApiInstance.interceptors.response.use(
    responseInterceptor.success,
    responseInterceptor.error
  );

  return {
    provide: {
      api: mainApiInstance
    }
  };
});
